import { parse } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

import { TIMEZONE_OFFSETS } from '@/constants';

export function getUrlFormattedDate(date: Date) {
  return date.toLocaleDateString('en-US').replace(/\//g, '-');
}

export function formatDateWithPattern(date: Date, format: string): string {
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
}

export function formatDateString(dateString: string, format: string): string {
  const [year, month, day, time] = dateString.split(/[-T]/);
  const [hours, minutes, seconds] = (time || '').split(':');

  if (!year || !month || !day) {
    throw new Error('Invalid date string provided');
  }

  return format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours || '00')
    .replace('mm', minutes || '00')
    .replace('ss', seconds?.split('.')[0] || '00');
}

export function formatURLDate(dateString: string) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const formattedDate = `${month}-${day}-${year}`;
  return formattedDate;
}

export const combineDateTime = (date: Date | string, time: Date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const [month, day, year] = formatDateWithPattern(date, 'MM-dd-yyyy').split(
    '-',
  );

  const hours = time
    ? new Date(time).getHours().toString().padStart(2, '0')
    : '00';
  const minutes = time
    ? new Date(time).getMinutes().toString().padStart(2, '0')
    : '00';

  return new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1, //January is 0, February is 1, etc.
    parseInt(day, 10),
    parseInt(hours, 10),
    parseInt(minutes, 10),
  );
};

export const formatTime = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}${minutes}`;
};

export const appendRevisionComment = (
  existingComment: string | undefined | null,
  newComment: string,
): string => {
  if (!existingComment) {
    return newComment;
  }

  const trimmedExisting = existingComment.trim();
  const trimmedNew = newComment.trim();

  if (trimmedExisting === trimmedNew) {
    return trimmedExisting;
  }

  return `${trimmedExisting}. ${trimmedNew}`;
};

export const convertTime = (
  time: string | undefined,
  newTimeZone: string | null,
  originalTimeZone: string | null,
): string => {
  try {
    if (!time) return '';
    if (
      newTimeZone === '' ||
      originalTimeZone === '' ||
      newTimeZone === null ||
      originalTimeZone === null
    )
      return time;

    const parsedTime = parse(
      time,
      time.includes(':') ? 'h:mm a' : 'HHmm',
      new Date(),
    );

    const originalDate = toZonedTime(parsedTime, originalTimeZone);
    const convertedDate = toZonedTime(originalDate, newTimeZone);

    return format(convertedDate, 'h:mm a zzz', {
      timeZone: newTimeZone,
    });
  } catch (error) {
    console.error('Error converting timezone:', error);
    return time ?? '';
  }
};

export const formatOpuTime = (
  date: Date = new Date(),
  timeZone: string = 'America/Chicago',
) => {
  try {
    const zonedDate = toZonedTime(date, timeZone);
    return format(zonedDate, 'HHmm', { timeZone });
  } catch (error) {
    console.error('Error formatting OPU time:', error);
    return date
      .toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(':', '');
  }
};

export const toCentralTime = (
  time: string,
  timezone: string = 'America/Chicago',
) => {
  try {
    if (!time)
      return format(toZonedTime(new Date(), 'America/Chicago'), 'HHmm');

    const hours = parseInt(time.substring(0, 2));
    const minutes = parseInt(time.substring(2, 4));

    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);

    const offset = TIMEZONE_OFFSETS[timezone];
    const adjustedHours = hours + offset;
    date.setUTCHours(adjustedHours, minutes);

    return format(toZonedTime(date, 'America/Chicago'), 'HHmm', {
      timeZone: 'America/Chicago',
    });
  } catch (error) {
    console.error('Error converting timezone:', error);
    return format(toZonedTime(new Date(), 'America/Chicago'), 'HHmm');
  }
};

export const formatShortSessionId = (sessionId: string | null | undefined) =>
  sessionId ? sessionId.slice(0, 8).toUpperCase() : '—';

export const sanitizeForUrl = (name: string): string => {
  return name
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, '')
    ?.replace(/\s+/g, '-')
    ?.replace(/-+/g, '-')
    ?.trim();
};

export const calculateIvcDate = (ivfDate: Date | string): Date => {
  const date = new Date(ivfDate);
  const utcDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );

  utcDate.setUTCDate(utcDate.getUTCDate() + 1);
  utcDate.setUTCHours(10, 0, 0, 0);

  return utcDate;
};

/**
 * IVC date as a pure local calendar date: the fertilization calendar day + 1,
 * anchored at local noon.
 *
 * Deliberately avoids Date.UTC / getUTC* / setUTC*. Reading the UTC calendar day
 * of a locally parsed timestamp shifts the result forward once the local time of
 * day crosses UTC midnight, which compounds with the +1 and yields the
 * fertilization date + 2 for technicians stripping late in the day.
 */
export const calculateIvcDateLocal = (ivfDate: Date | string): Date => {
  if (typeof ivfDate === 'string') {
    // Handles "yyyy-MM-dd" (GraphQL Date scalar) and "yyyy-MM-ddTHH:mm:ss"
    // (REST DateTime, serialized without an offset) identically.
    const [year, month, day] = ivfDate.slice(0, 10).split('-').map(Number);

    if ([year, month, day].some((part) => !Number.isFinite(part))) {
      return calculateIvcDate(ivfDate);
    }

    return new Date(year, month - 1, day + 1, 12, 0, 0, 0);
  }

  return new Date(
    ivfDate.getFullYear(),
    ivfDate.getMonth(),
    ivfDate.getDate() + 1,
    12,
    0,
    0,
    0,
  );
};
